import type { ReactElement } from 'react';

import { useApp } from '@weke/ui';
import { formatMoney, formatSignedMoney, formatTimestamp } from '@weke/ui';
import { explainAlert, explainRejection } from '@weke/ui';
import { Panel } from '@weke/ui';

/**
 * Alerts and the journal of facts the event stream reports that no table owns —
 * a dealer acceptance, a position close, a rejected order.
 *
 * This is where the K-6 case becomes readable: a dealer request can produce a
 * `DEALER_ACCEPTED` event **and** a `REJECTED` response on the order path. Both
 * are true and both are shown. Hiding either one would be the lie.
 *
 * `mode` selects which half the toolbox tab shows; both read the same store, so
 * nothing is fetched twice.
 */
export function ActivityPanel(
  props: { readonly mode?: 'alerts' | 'journal' } = {},
): ReactElement {
  const app = useApp();
  const mode = props.mode ?? 'journal';
  const digits = app.account?.digits ?? 2;
  const rows = app.book.activity;

  if (mode === 'alerts') {
    return (
      <Panel id="alerts" title="Alerts" badge={`${app.alerts.length}`}>
        <ul className="flex flex-col text-2xs">
          {app.alerts.length === 0 ? (
            <li className="muted px-2 py-3 text-center">No alerts.</li>
          ) : null}
          {app.alerts.map((alert) => (
            <li
              key={alert.id}
              className="flex gap-2 border-b border-[#232323] px-2 py-1"
              data-testid="alert-row"
            >
              <span className="num faint w-[92px] flex-none">{formatTimestamp(alert.at)}</span>
              <span className="text-warning">⚠</span>
              <span className="flex-1">
                <span className="text-text">{explainAlert(alert.kind)}</span>{' '}
                <span className="faint">{alert.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </Panel>
    );
  }

  return (
    <Panel id="journal" title="Journal" badge={`${rows.length}`}>
      <ul className="flex flex-col text-2xs" data-testid="journal">
        {rows.length === 0 ? (
          <li className="muted px-2 py-3 text-center">
            Nothing yet. Dealer decisions, closes and rejections appear here.
          </li>
        ) : null}
        {rows.map((row) => {
          if (row.kind === 'ALERT') return null;
          const label =
            row.kind === 'ORDER_REJECTED'
              ? `Order rejected — ${row.code === null ? '' : explainRejection(row.code).title}`
              : row.kind === 'DEALER_ACCEPTED'
                ? 'Dealer accepted the request'
                : row.kind === 'POSITION_CLOSED'
                  ? 'Position closed'
                  : row.kind === 'POSITION_REDUCED'
                    ? 'Position reduced'
                    : 'Stop out';
          const amount = row.amountMinor;
          return (
            <li
              key={row.id}
              className="flex gap-2 border-b border-[#232323] px-2 py-1"
              data-testid="activity-row"
            >
              <span className="num faint w-[92px] flex-none">{formatTimestamp(row.at)}</span>
              <span className={row.kind === 'ORDER_REJECTED' ? 'sell' : 'muted'}>
                {row.kind === 'DEALER_ACCEPTED' ? '✔' : row.kind === 'ORDER_REJECTED' ? '✕' : '•'}
              </span>
              <span className="flex-1">{label}</span>
              {amount === null ? null : (
                <span
                  className={`num ${amount.startsWith('-') ? 'loss' : 'gain'}`}
                  title="Realised P&L on this fact, from the server"
                >
                  {formatSignedMoney(amount, digits)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="faint px-2 py-1 text-2xs">
        Balances move only through deals.{' '}
        {formatMoney(app.account?.floating_minor ?? null, digits)} is floating right now.
      </p>
    </Panel>
  );
}
